import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { Buttons } from "@/components/atoms/Buttons";
import { useQuery } from "@tanstack/react-query";
import { showAlert } from "@/components/containers/Alert";
import { InputGroup } from "@/components/molecules/InputGroup";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { JoinTemplate } from "@/components/templates/JoinTemplate";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { SelectControl } from "@/components/containers/Controls/SelectControl";
import { useRecoilValue } from "recoil";
import { JoinProcessBar } from "@/components/atoms/JoinProcessBar";
import { Guardian, Student } from "@/common/types/student";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useJoinFormHandlers } from "@/common/hooks/useJoinFormHandlers";
import { agreeState, typeState } from "@/common/states/agree";
import { GET_GUARDIAN, GET_STUDENT } from "@/common/querykeys";

const schema = yup.object({
  join: yup.object({
    userId: yup
      .string()
      .required("ID는 필수 입력입니다.")
      .matches(/^[a-zA-Z0-9]+$/, "ID는 영문 또는 숫자만 입력 가능합니다.")
      .min(4, "ID는 최소 4자 이상이어야 합니다.")
      .max(20, "ID는 최대 20자 이하이여야 합니다."),
    password: yup
      .string()
      .required("비밀번호는 필수 입력입니다.")
      .min(8, "비밀번호는 최소 8자 이상이어야 합니다.")
      .max(20, "비밀번호는 최대 20자 이하이어야 합니다."),
    passwordCheck: yup
      .string()
      .oneOf([yup.ref("password")], "비밀번호가 일치하지 않습니다.")
      .required("비밀번호 재확인은 필수 입력입니다."),
    accountFindQuestion: yup
      .string()
      .required("계정 찾기 질문은 필수 입력입니다."),
    questionAnswer: yup
      .string()
      .required("질문에 대한 답변은 필수 입력입니다."),
    agreementTime1: yup
      .date()
      .required("서비스 이용 안내 동의 시간은 필수 입력입니다.")
      .typeError("동의 시간 1은 유효한 날짜여야 합니다."),
    agreementTime2: yup
      .date()
      .required(
        "개인정보 수집∙이용 및 제 3자 제공 동의 시간은 필수 입력입니다."
      )
      .typeError("동의 시간 2는 유효한 날짜여야 합니다."),
  }),
});

export const JoinAccountFormPage = () => {
  const navigate = useNavigate();
  const agree = useRecoilValue(agreeState);
  const joinFormType = useRecoilValue(typeState);
  const {
    joinComm,
    handleIdChange,
    handlePwChange,
    handleQnAChange,
    handlePwCheckChange,
    handleAccountFindQuestionChange,
  } = useJoinFormHandlers();
  const [isIdCheck, setIsIdCheck] = useState(!!joinComm.userId);

  const { control, handleSubmit } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      join: {
        userId: joinComm.userId,
        password: joinComm.password,
        passwordCheck: joinComm.passwordCheck,
        accountFindQuestion: joinComm.accountFindQuestion,
        questionAnswer: joinComm.questionAnswer,
        agreementTime1: agree?.agreementTime1!,
        agreementTime2: agree?.agreementTime2!,
      },
    },
  });

  const { data: guardians } = useQuery<Guardian[]>({
    queryKey: [GET_GUARDIAN],
    queryFn: async () => {
      return await request<Guardian[]>({
        method: "GET",
        url: "/guardian",
      });
    },
  });

  const { data: students } = useQuery<Student[]>({
    queryKey: [GET_STUDENT],
    queryFn: async () => {
      return await request<Student[]>({
        method: "GET",
        url: "/student",
      });
    },
  });

  const onSubmit = () => {
    try {
      joinFormType?.selectedType === 1
        ? navigate("/auth/join/student-form")
        : navigate("/auth/join/guardian-form");
    } catch (error) {
      console.error("@@@ error: ", error);
    }
  };

  const handleClick = () => {
    const userId = joinComm.userId;

    const isValidFormat = /^[a-zA-Z0-9]+$/.test(userId);

    if (userId.length === 0) {
      showAlert("ID를 입력하지 않았습니다. \n 입력 후 확인해 주세요.");
      setIsIdCheck(false);
    } else if (userId.length < 4) {
      showAlert("ID가 4자리 미만입니다. \n 다시 입력해 주세요.");
      setIsIdCheck(false);
    } else if (!isValidFormat) {
      showAlert(
        "ID는 영문 또는 숫자만 입력 가능합니다. \n 다시 입력해 주세요."
      );
      setIsIdCheck(false);
    } else {
      const isDuplicate =
        students?.some((student) => student.userId === userId) ||
        guardians?.some((guardian) => guardian.userId === userId);

      if (isDuplicate) {
        showAlert(
          "이미 사용중인 ID 입니다. \n 다른 ID로 회원가입을 진행해 주세요."
        );
        setIsIdCheck(false);
      } else {
        showAlert("사용 가능한 ID 입니다.");
        setIsIdCheck(true);
      }
    }
  };

  useEffect(() => {
    if (joinComm.userId && isIdCheck) {
      setIsIdCheck(true);
    } else {
      setIsIdCheck(false);
    }
  }, [joinComm.userId, isIdCheck]);

  return (
    <JoinTemplate>
      <Form
        processBar={<JoinProcessBar stepNow={4} stepTotal={6} />}
        title="가입정보 입력"
        submitBtn={
          <Buttons
            type="submit"
            size="lg"
            color="primary100"
            layout="solid"
            label="다음"
            disabled={!isIdCheck}
            onClick={handleSubmit(onSubmit)}
          />
        }
        backBtn={
          <Buttons
            type="button"
            size="lg"
            color="primary100"
            layout="text"
            label="뒤로"
            onClick={() => navigate("/auth/join/agree-use")}
          />
        }
      >
        <>
          <FormInputBox formTitle="계정정보 입력">
            <>
              <InputGroup direction="row" gridColumn="70% 30%">
                <>
                  <TextControl
                    control={control}
                    name="join.userId"
                    label="아이디"
                    placeholder="아이디를 입력하세요."
                    required={true}
                    value={joinComm.userId}
                    onChange={handleIdChange}
                    instructions="id"
                  />
                  <Buttons
                    type="button"
                    size="form"
                    layout="solid"
                    color="primary100"
                    label="ID 확인"
                    onClick={handleClick}
                  />
                </>
              </InputGroup>
              <TextControl
                control={control}
                type="password"
                name="join.password"
                label="비밀번호"
                placeholder="비밀번호를 입력하세요."
                required={true}
                value={joinComm.password}
                onChange={handlePwChange}
                instructions="pw"
              />
              <TextControl
                control={control}
                type="password"
                name="join.passwordCheck"
                label="비밀번호 확인"
                placeholder="비밀번호를 재입력 하세요."
                required={true}
                value={joinComm.passwordCheck}
                onChange={handlePwCheckChange}
                instructions="pwCheck"
              />
              <Controller
                name="join.accountFindQuestion"
                control={control}
                render={({ field }) => (
                  <SelectControl
                    control={control}
                    {...field}
                    placeholder="계정 찾기 질문을 선택하세요."
                    required={true}
                    value={joinComm.accountFindQuestion}
                    onChange={handleAccountFindQuestionChange}
                    label="계정 찾기 질문"
                    options={[
                      {
                        label: "할아버지의 성함은 무엇인가요?",
                        value: "grandFather",
                      },
                      {
                        label: "할머니의 성함은 무엇인가요?",
                        value: "grandMa",
                      },
                      { label: "아버지의 성함은 무엇인가요?", value: "father" },
                      { label: "어머니의 성함은 무엇인가요?", value: "mother" },
                    ]}
                  />
                )}
              />
              {joinComm.accountFindQuestion && (
                <TextControl
                  control={control}
                  name="join.questionAnswer"
                  label="질문에 대한 답변"
                  placeholder="답변을 입력하세요."
                  required={true}
                  value={joinComm.questionAnswer}
                  onChange={handleQnAChange}
                  instructions="answer"
                />
              )}
            </>
          </FormInputBox>
        </>
      </Form>
    </JoinTemplate>
  );
};
